import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '../../constants/routes';
import { fetchUserData, resetAuth, clearRedirectToLogin } from '../../redux/auth.slice';
import { setIsAuthVerified } from '../../redux/basic.slice';
import { AppDispatch, persistor } from '../../redux/store';

interface Props {
    children: React.ReactNode;
}

const AuthCheck: React.FC<Props> = ({ children }) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const shouldRedirectToLogin = useSelector((state: any) => state.auth.shouldRedirectToLogin);

    // On every page load the token is checked FIRST, on its own. Nothing else is allowed to call
    // the API until this settles (AppLayout holds the loader on `isAuthVerified`), so an expired
    // token produces one clean 401 from get-user-details instead of a burst of failed requests.
    useEffect(() => {
        if (localStorage.getItem('token')) {
            // createAsyncThunk's promise resolves for both fulfilled and rejected, so this marks
            // the check as COMPLETE either way — on failure the reducer has already cleared
            // isAuthenticated and PrivateRoute sends the user to login.
            dispatch(fetchUserData()).finally(() => {
                dispatch(setIsAuthVerified(true));
            });
        } else {
            // No token: nothing to verify, go straight to the public/login routes.
            dispatch(resetAuth());
            dispatch(setIsAuthVerified(true));
        }
    }, []);

    useEffect(() => {
        if (shouldRedirectToLogin) {
            persistor.pause(); // stop redux-persist from re-writing state after reset
            persistor.purge();
            localStorage.clear();
            dispatch(clearRedirectToLogin());
            navigate(ROUTES.LOGIN);
        }
    }, [shouldRedirectToLogin, navigate]);

    return <>{children}</>;
};

export default AuthCheck;
